import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import TerminalIcon from '@mui/icons-material/Terminal';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTheme, alpha } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { DELETE, GET, PATCH, POST } from '../../../configs/api';
import { getUser } from '../../../store/slices/userSlice';
import { api } from '../../../utils/constants';
import { canManageAiPromptTemplates } from '../../../utils/permissions';
import {
  PLATFORM_TEMPLATE_VALUE,
  getAiPromptKindLabel,
  getInitialTemplateSelection,
} from './AiPromptTemplatesHelper';
import { getModernTextFieldSx } from './modernFormFieldStyles';

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

const emptyEditor = () => ({
  name: '',
  system_prompt: '',
  user_prompt_template: '',
  is_default: false,
});

const previewText = (value, max = 140) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return 'Uses platform default for this half';
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
};

const getSurface = (isDarkMode) => ({
  barBg: isDarkMode
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 55%, rgba(2, 6, 23, 0.96) 100%)'
    : 'linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(255, 255, 255, 0.96) 48%, rgba(241, 245, 249, 0.94) 100%)',
  barBorder: isDarkMode ? 'rgba(148, 163, 184, 0.28)' : 'rgba(15, 23, 42, 0.12)',
  barShadow: isDarkMode
    ? '0 0 0 1px rgba(255,255,255,0.04), 0 12px 32px -16px rgba(0, 0, 0, 0.7)'
    : '0 10px 28px -18px rgba(15, 23, 42, 0.22), 0 1px 0 rgba(255,255,255,0.9) inset',
  fieldBg: isDarkMode ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.96)',
  fieldBorder: isDarkMode ? 'rgba(148, 163, 184, 0.28)' : 'rgba(15, 23, 42, 0.14)',
  dialogPaper: isDarkMode
    ? 'linear-gradient(160deg, #1e293b 0%, #0f172a 45%, #020617 100%)'
    : 'linear-gradient(160deg, #ffffff 0%, #f8fafc 52%, #f1f5f9 100%)',
  dialogBorder: isDarkMode ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.1)',
  promptPanelBg: isDarkMode
    ? 'linear-gradient(145deg, rgba(30, 41, 59, 0.92) 0%, rgba(15, 23, 42, 0.96) 100%)'
    : 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
  promptPanelBorder: isDarkMode ? 'rgba(148, 163, 184, 0.28)' : 'rgba(15, 23, 42, 0.12)',
  codeBg: isDarkMode ? 'rgba(2, 6, 23, 0.72)' : 'rgba(15, 23, 42, 0.035)',
  codeColor: isDarkMode ? '#e2e8f0' : '#0f172a',
  accent: isDarkMode ? '#e2e8f0' : '#0f172a',
  muted: isDarkMode ? 'rgba(203, 213, 225, 0.72)' : 'rgba(71, 85, 105, 0.9)',
  cardBg: isDarkMode ? 'rgba(30, 41, 59, 0.55)' : 'rgba(255, 255, 255, 0.88)',
  cardHover: isDarkMode ? 'rgba(51, 65, 85, 0.75)' : 'rgba(241, 245, 249, 0.98)',
  chipBg: isDarkMode ? 'rgba(148, 163, 184, 0.16)' : 'rgba(15, 23, 42, 0.06)',
  chipColor: isDarkMode ? '#e2e8f0' : '#0f172a',
  selectedItem: isDarkMode ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.08)',
  hoverItem: isDarkMode ? 'rgba(148, 163, 184, 0.14)' : 'rgba(15, 23, 42, 0.05)',
  shine: isDarkMode
    ? 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.06) 50%, transparent 70%)'
    : 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.7) 50%, transparent 70%)',
  topLine: isDarkMode
    ? 'linear-gradient(90deg, transparent, rgba(226, 232, 240, 0.55), transparent)'
    : 'linear-gradient(90deg, transparent, rgba(15, 23, 42, 0.55), transparent)',
  titleWash: isDarkMode
    ? 'linear-gradient(90deg, rgba(148, 163, 184, 0.12), transparent)'
    : 'linear-gradient(90deg, rgba(15, 23, 42, 0.05), transparent)',
});

const selectSx = (surface, isDarkMode) => ({
  minWidth: 160,
  borderRadius: '12px',
  backgroundColor: surface.fieldBg,
  backdropFilter: 'blur(10px)',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: surface.fieldBorder,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: surface.accent,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: surface.accent,
    borderWidth: 1.5,
  },
  '& .MuiSelect-select': {
    fontSize: '0.84rem',
    fontWeight: 600,
    py: 1.1,
  },
  boxShadow: isDarkMode
    ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
    : 'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 2px rgba(15,23,42,0.04)',
});

const menuPaperSx = (surface, isDarkMode) => ({
  mt: 0.75,
  borderRadius: '14px',
  border: '1px solid',
  borderColor: surface.dialogBorder,
  backgroundImage: surface.dialogPaper,
  backgroundColor: isDarkMode ? '#0f172a' : '#fff',
  boxShadow: isDarkMode
    ? '0 18px 48px -20px rgba(0,0,0,0.75)'
    : '0 18px 40px -22px rgba(15, 23, 42, 0.28)',
  overflow: 'hidden',
  '& .MuiMenuItem-root': {
    fontSize: '0.84rem',
    fontWeight: 500,
    borderRadius: '10px',
    mx: 0.75,
    my: 0.25,
  },
  '& .MuiMenuItem-root.Mui-selected': {
    backgroundColor: surface.selectedItem,
  },
  '& .MuiMenuItem-root:hover': {
    backgroundColor: surface.hoverItem,
  },
});

const primaryBtnSx = (isDarkMode) => ({
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: '11px',
  px: 2,
  color: isDarkMode ? '#0f172a' : '#fff',
  backgroundColor: isDarkMode ? '#e2e8f0' : '#0f172a',
  backgroundImage: 'none',
  boxShadow: isDarkMode
    ? '0 10px 24px -12px rgba(0, 0, 0, 0.65)'
    : '0 10px 22px -12px rgba(15, 23, 42, 0.45)',
  '&:hover': {
    backgroundColor: isDarkMode ? '#f8fafc' : '#000000',
    backgroundImage: 'none',
  },
});

function PromptPreviewPanel({
  title,
  badge,
  value,
  surface,
  isDarkMode,
  editable = false,
  onChange,
  helperText,
  disabled,
  maxLength = 900,
}) {
  const length = String(value || '').length;
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: surface.promptPanelBorder,
        backgroundImage: surface.promptPanelBg,
        boxShadow: isDarkMode
          ? '0 0 0 1px rgba(255,255,255,0.04), 0 16px 36px -24px rgba(0,0,0,0.8)'
          : '0 12px 28px -22px rgba(15, 23, 42, 0.2)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: surface.topLine,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.75,
          pt: 1.5,
          pb: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TerminalIcon sx={{ fontSize: 16, color: surface.accent }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: surface.accent }}>
            {title}
          </Typography>
          {badge ? (
            <Chip
              size="small"
              label={badge}
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 700,
                bgcolor: surface.chipBg,
                color: surface.chipColor,
                border: '1px solid',
                borderColor: surface.promptPanelBorder,
              }}
            />
          ) : null}
        </Stack>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: surface.muted }}>
          {length}/{maxLength}
        </Typography>
      </Box>
      {editable ? (
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={10}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder="Leave blank to keep the platform default"
          sx={{
            px: 1.25,
            pb: 1.25,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              bgcolor: surface.codeBg,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.8rem',
              color: surface.codeColor,
              lineHeight: 1.55,
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: alpha(surface.accent, 0.35) },
              '&.Mui-focused fieldset': { borderColor: surface.accent },
            },
          }}
        />
      ) : (
        <Box
          sx={{
            mx: 1.25,
            mb: 1.25,
            px: 1.5,
            py: 1.25,
            borderRadius: '12px',
            bgcolor: surface.codeBg,
            minHeight: 72,
          }}
        >
          <Typography
            component="pre"
            sx={{
              m: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.78rem',
              lineHeight: 1.55,
              color: value ? surface.codeColor : surface.muted,
              fontStyle: value ? 'normal' : 'italic',
            }}
          >
            {value || 'Uses platform default'}
          </Typography>
        </Box>
      )}
      {helperText ? (
        <Typography sx={{ px: 1.75, pb: 1.35, fontSize: '0.72rem', color: surface.muted }}>
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Modern prompt-template picker for owners / GMs / accountants / super_admins.
 */
export default function AiPromptTemplatesBar({
  kinds = [],
  selection = {},
  onSelectionChange,
  disabled = false,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const surface = getSurface(isDarkMode);
  const themeUser = useSelector(getUser);
  const canManage = canManageAiPromptTemplates(themeUser);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [templates, setTemplates] = React.useState([]);
  const [platformDefaults, setPlatformDefaults] = React.useState({});
  const [activeKind, setActiveKind] = React.useState(kinds[0] || 'proposal_refine');

  const [manageOpen, setManageOpen] = React.useState(false);
  const [fieldPreviewOpen, setFieldPreviewOpen] = React.useState(false);
  const [previewTemplate, setPreviewTemplate] = React.useState(null);
  const [editor, setEditor] = React.useState(emptyEditor);
  const [editingId, setEditingId] = React.useState(null);
  const [renameId, setRenameId] = React.useState(null);
  const [renameName, setRenameName] = React.useState('');

  const kindTemplates = React.useMemo(
    () => templates.filter((t) => t.kind === activeKind),
    [templates, activeKind],
  );

  const defaultId = kindTemplates.find((t) => t.is_default)?.id || null;
  const selectValue = selection[activeKind] || PLATFORM_TEMPLATE_VALUE;
  const selectedTemplate = kindTemplates.find((t) => t.id === selectValue) || null;

  /**
   * Keep blank halves blank (null/empty → '') so company templates still inherit
   * platform updates for that half. Only platform selection pre-fills platform text.
   */
  const hydrateEditorFromSelection = React.useCallback(() => {
    const selected = kindTemplates.find((t) => t.id === selectValue);
    const plat = platformDefaults[activeKind] || {};

    if (selected) {
      setEditingId(selected.id);
      setEditor({
        name: selected.name || '',
        system_prompt: selected.system_prompt == null ? '' : String(selected.system_prompt),
        user_prompt_template: selected.user_prompt_template == null
          ? ''
          : String(selected.user_prompt_template),
        is_default: Boolean(selected.is_default),
      });
      return;
    }

    setEditingId(null);
    setEditor({
      name: '',
      system_prompt: plat.system_prompt == null ? '' : String(plat.system_prompt),
      user_prompt_template: plat.user_prompt_template == null
        ? ''
        : String(plat.user_prompt_template),
      is_default: false,
    });
  }, [kindTemplates, selectValue, activeKind, platformDefaults]);

  const applyInitialSelection = React.useCallback((rows) => {
    if (!onSelectionChange || !kinds.length) return;
    const next = {};
    let needsUpdate = false;
    kinds.forEach((kind) => {
      const current = selection[kind];
      if (current) {
        next[kind] = current;
        return;
      }
      next[kind] = getInitialTemplateSelection(rows, kind);
      needsUpdate = true;
    });
    if (needsUpdate) onSelectionChange({ ...selection, ...next });
  }, [kinds, onSelectionChange, selection]);

  const loadTemplates = React.useCallback(async () => {
    setLoading(true);
    setError('');
    const { status, data, message } = await GET(api.getAiPromptTemplates, {});
    setLoading(false);
    if (status !== 200 || !data) {
      setError(message || 'Could not load prompt templates.');
      return;
    }
    const rows = Array.isArray(data.templates) ? data.templates : [];
    setTemplates(rows);
    setPlatformDefaults(data.platform_defaults || {});
    applyInitialSelection(rows);
  }, [applyInitialSelection]);

  React.useEffect(() => {
    if (!canManage) return undefined;
    loadTemplates();
    return undefined;
    // Intentionally load once when an eligible role mounts this bar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);

  React.useEffect(() => {
    if (kinds.length && !kinds.includes(activeKind)) {
      setActiveKind(kinds[0]);
    }
  }, [kinds, activeKind]);

  // Sync editor from the active kind + template selection (create vs edit-in-place).
  React.useEffect(() => {
    if (!canManage || loading) return;
    hydrateEditorFromSelection();
  }, [canManage, loading, hydrateEditorFromSelection]);

  if (!canManage) return null;

  const setKindSelection = (kind, value) => {
    onSelectionChange?.({ ...selection, [kind]: value });
  };

  const changeActiveKind = (kind) => {
    // Selection for the new kind drives hydrate (edit-in-place vs platform create).
    setActiveKind(kind);
  };

  const openEditDialog = (template) => {
    setPreviewTemplate(null);
    setManageOpen(false);
    setEditingId(template.id);
    setEditor({
      name: template.name || '',
      system_prompt: template.system_prompt == null ? '' : String(template.system_prompt),
      user_prompt_template: template.user_prompt_template == null
        ? ''
        : String(template.user_prompt_template),
      is_default: Boolean(template.is_default),
    });
    if (template.id && selection[activeKind] !== template.id) {
      onSelectionChange?.({ ...selection, [activeKind]: template.id });
    }
  };

  const openPreview = (template) => {
    setPreviewTemplate(template);
  };

  const openFieldPreview = () => {
    setFieldPreviewOpen(true);
  };

  const handleSave = async () => {
    const name = editor.name.trim();
    if (!name) {
      setError('Enter a template name before saving.');
      return;
    }
    if (!editor.system_prompt.trim() && !editor.user_prompt_template.trim()) {
      setError('Provide a system prompt and/or a user prompt template.');
      return;
    }

    setBusy(true);
    setError('');
    // Empty halves stay empty so the API stores null and generate inherits platform.
    const body = {
      name,
      system_prompt: editor.system_prompt.trim() || null,
      user_prompt_template: editor.user_prompt_template.trim() || null,
      is_default: Boolean(editor.is_default),
    };

    const targetId = editingId || (selectedTemplate?.id ?? null);
    const wasEditing = Boolean(targetId);
    let result;
    if (targetId) {
      result = await PATCH(`${api.updateAiPromptTemplate}/${targetId}`, {}, body);
    } else {
      result = await POST(api.createAiPromptTemplate, {}, { ...body, kind: activeKind });
    }
    setBusy(false);

    if ((wasEditing && result.status !== 200) || (!wasEditing && result.status !== 201) || !result.data) {
      setError(result.message || result.data?.message || 'Could not save prompt template.');
      return;
    }

    const savedId = result.data?.id || targetId;
    await loadTemplates();
    if (savedId && selection[activeKind] !== savedId) {
      onSelectionChange?.({ ...selection, [activeKind]: savedId });
    }
  };

  const handleDelete = async (id) => {
    setBusy(true);
    setError('');
    const result = await DELETE(`${api.deleteAiPromptTemplate}/${id}`);
    setBusy(false);
    if (result.status !== 200) {
      setError(result.message || 'Could not delete prompt template.');
      return;
    }
    if (selection[activeKind] === id) {
      setKindSelection(activeKind, PLATFORM_TEMPLATE_VALUE);
    }
    if (previewTemplate?.id === id) setPreviewTemplate(null);
    await loadTemplates();
  };

  const handleSetDefault = async (id, isDefault) => {
    setBusy(true);
    setError('');
    const result = await PATCH(`${api.setDefaultAiPromptTemplate}/${id}/default`, {}, { is_default: isDefault });
    setBusy(false);
    if (result.status !== 200) {
      setError(result.message || 'Could not update default template.');
      return;
    }
    await loadTemplates();
  };

  const commitRename = async () => {
    const trimmed = renameName.trim();
    if (!trimmed || !renameId) return;
    setBusy(true);
    const result = await PATCH(`${api.updateAiPromptTemplate}/${renameId}`, {}, { name: trimmed });
    setBusy(false);
    setRenameId(null);
    setRenameName('');
    if (result.status !== 200) {
      setError(result.message || 'Could not rename template.');
      return;
    }
    await loadTemplates();
  };

  const dialogPaperSx = {
    borderRadius: '20px',
    border: '1px solid',
    borderColor: surface.dialogBorder,
    backgroundImage: surface.dialogPaper,
    backgroundColor: isDarkMode ? '#0f172a' : '#fff',
    boxShadow: isDarkMode
      ? '0 28px 80px -28px rgba(0,0,0,0.85)'
      : '0 28px 70px -30px rgba(15, 23, 42, 0.28)',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ width: '100%', mb: 1.25 }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          gap: 1.1,
          flexWrap: 'wrap',
          px: 1.5,
          py: 1.15,
          borderRadius: '16px',
          border: '1px solid',
          borderColor: surface.barBorder,
          backgroundImage: surface.barBg,
          boxShadow: surface.barShadow,
          backdropFilter: 'blur(14px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: surface.shine,
            pointerEvents: 'none',
          },
        }}
      >
        <Chip
          icon={<AutoAwesomeIcon sx={{ fontSize: '0.95rem !important' }} />}
          label="AI prompts"
          size="small"
          sx={{
            fontWeight: 800,
            letterSpacing: '0.02em',
            color: surface.chipColor,
            bgcolor: surface.chipBg,
            border: '1px solid',
            borderColor: surface.barBorder,
            '& .MuiChip-icon': { color: 'inherit' },
          }}
        />

        <FormControl size="small" disabled={disabled || loading || busy} sx={{ minWidth: 150 }}>
          <InputLabel id="ai-prompt-kind-label" sx={{ fontWeight: 600 }}>Prompt for</InputLabel>
          <Select
            labelId="ai-prompt-kind-label"
            label="Prompt for"
            value={activeKind}
            onChange={(event) => changeActiveKind(event.target.value)}
            sx={selectSx(surface, isDarkMode)}
            MenuProps={{ PaperProps: { sx: menuPaperSx(surface, isDarkMode) } }}
          >
            {kinds.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {getAiPromptKindLabel(kind)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" disabled={disabled || loading || busy} sx={{ minWidth: 200, flex: '1 1 200px' }}>
          <InputLabel id="ai-prompt-template-label" sx={{ fontWeight: 600 }}>Template</InputLabel>
          <Select
            labelId="ai-prompt-template-label"
            label="Template"
            value={selectValue}
            onChange={(event) => setKindSelection(activeKind, event.target.value)}
            sx={selectSx(surface, isDarkMode)}
            MenuProps={{ PaperProps: { sx: menuPaperSx(surface, isDarkMode) } }}
            renderValue={(value) => {
              if (value === PLATFORM_TEMPLATE_VALUE) return 'Platform default';
              const match = kindTemplates.find((t) => t.id === value);
              return match?.name || 'Template';
            }}
          >
            <MenuItem value={PLATFORM_TEMPLATE_VALUE}>
              <Stack spacing={0.15}>
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>Platform default</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                  Built-in prompts for this action
                </Typography>
              </Stack>
            </MenuItem>
            {kindTemplates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.85, width: '100%' }}>
                  {defaultId === template.id ? (
                    <StarIcon sx={{ fontSize: 16, color: 'warning.main', mt: 0.2 }} />
                  ) : (
                    <StarBorderIcon sx={{ fontSize: 16, color: 'text.disabled', mt: 0.2 }} />
                  )}
                  <Stack spacing={0.15} sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>{template.name}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {previewText(template.system_prompt || template.user_prompt_template, 64)}
                    </Typography>
                  </Stack>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Template name"
          value={editor.name}
          onChange={(event) => setEditor((prev) => ({ ...prev, name: event.target.value }))}
          disabled={disabled || loading || busy}
          sx={{
            ...getModernTextFieldSx(isDarkMode),
            minWidth: 160,
            flex: '0 1 200px',
            '& .MuiInputBase-root': { borderRadius: '12px' },
          }}
        />

        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={<BookmarkBorderIcon fontSize="small" />}
          onClick={handleSave}
          disabled={disabled || loading || busy || !editor.name.trim()}
          sx={{ ...primaryBtnSx(isDarkMode), whiteSpace: 'nowrap' }}
        >
          {busy ? 'Saving...' : editingId ? 'Update' : 'Save'}
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityOutlinedIcon fontSize="small" />}
          onClick={openFieldPreview}
          disabled={disabled || loading || busy}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '11px',
            whiteSpace: 'nowrap',
            borderColor: surface.fieldBorder,
            color: surface.accent,
            bgcolor: surface.fieldBg,
            '&:hover': {
              borderColor: surface.accent,
              bgcolor: surface.hoverItem,
            },
          }}
        >
          Preview
        </Button>

        <Tooltip title="Manage prompt templates">
          <span>
            <IconButton
              size="small"
              onClick={() => {
                setPreviewTemplate(null);
                setManageOpen(true);
              }}
              disabled={disabled || loading || busy}
              aria-label="Manage prompt templates"
              sx={{
                width: 36,
                height: 36,
                borderRadius: '11px',
                border: '1px solid',
                borderColor: surface.fieldBorder,
                bgcolor: surface.fieldBg,
                color: surface.accent,
                '&:hover': {
                  bgcolor: surface.hoverItem,
                },
              }}
            >
              {loading ? <CircularProgress size={16} color="inherit" /> : <SettingsOutlinedIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>

        <Typography
          variant="caption"
          sx={{
            color: error ? 'error.main' : surface.muted,
            fontSize: '0.74rem',
            fontWeight: 500,
            flex: '1 1 180px',
          }}
        >
          {error
            || (editingId
              ? `Editing: ${editor.name || 'template'}`
              : selectedTemplate
                ? `Active: ${selectedTemplate.name}`
                : 'Platform default active — save custom presets for this company.')}
        </Typography>
      </Box>

      <Box
        sx={{
          mt: 1.25,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          px: 0.25,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems="stretch"
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <PromptPreviewPanel
              title="System prompt"
              badge="model instructions"
              value={editor.system_prompt}
              surface={surface}
              isDarkMode={isDarkMode}
              editable
              disabled={disabled || loading || busy}
              onChange={(event) => setEditor((prev) => ({ ...prev, system_prompt: event.target.value }))}
              helperText="Leave blank to keep the platform system prompt. Max 900 characters."
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <PromptPreviewPanel
              title="User prompt"
              badge="template"
              value={editor.user_prompt_template}
              surface={surface}
              isDarkMode={isDarkMode}
              editable
              disabled={disabled || loading || busy}
              onChange={(event) => setEditor((prev) => ({ ...prev, user_prompt_template: event.target.value }))}
              helperText="Placeholders: {{note}}, {{allowed_types}}, {{trade}}, {{store_name}}, {{location}}, {{priority}}, {{nte}}. Combined with system ≤ 1400 chars."
            />
          </Box>
        </Stack>

        <FormControlLabel
          control={(
            <Checkbox
              checked={Boolean(editor.is_default)}
              onChange={(event) => setEditor((prev) => ({ ...prev, is_default: event.target.checked }))}
              disabled={disabled || loading || busy}
              sx={{ color: surface.accent, '&.Mui-checked': { color: surface.accent } }}
            />
          )}
          label={`Set as default for ${getAiPromptKindLabel(activeKind)}`}
          sx={{ color: surface.muted, ml: 0.25, '& .MuiFormControlLabel-label': { fontSize: '0.88rem', fontWeight: 600 } }}
        />
      </Box>

      {/* Read-only preview of current field values */}
      <Dialog
        open={fieldPreviewOpen}
        onClose={() => setFieldPreviewOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            pr: 6,
            pt: 2.25,
            background: surface.titleWash,
          }}
        >
          <Stack spacing={0.4}>
            <Typography component="span" sx={{ fontSize: '1.15rem', fontWeight: 800 }}>
              Prompt preview
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: surface.muted }}>
              {getAiPromptKindLabel(activeKind)}
              {editor.name.trim() ? ` · ${editor.name.trim()}` : ''}
            </Typography>
          </Stack>
          <IconButton
            onClick={() => setFieldPreviewOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <PromptPreviewPanel
            title="System prompt"
            badge="model instructions"
            value={editor.system_prompt}
            surface={surface}
            isDarkMode={isDarkMode}
          />
          <PromptPreviewPanel
            title="User prompt"
            badge="template"
            value={editor.user_prompt_template}
            surface={surface}
            isDarkMode={isDarkMode}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setFieldPreviewOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage + live prompt preview */}
      <Dialog
        open={manageOpen}
        onClose={() => !busy && setManageOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            pr: 6,
            pt: 2.25,
            background: surface.titleWash,
          }}
        >
          <Stack spacing={0.4}>
            <Typography component="span" sx={{ fontSize: '1.15rem', fontWeight: 800 }}>
              Prompt templates
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: surface.muted }}>
              {getAiPromptKindLabel(activeKind)} · click a card to preview prompts
            </Typography>
          </Stack>
          <IconButton
            onClick={() => setManageOpen(false)}
            disabled={busy}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {kindTemplates.length === 0 ? (
            <Box
              sx={{
                py: 5,
                textAlign: 'center',
                borderRadius: '16px',
                border: '1px dashed',
                borderColor: surface.fieldBorder,
                bgcolor: surface.codeBg,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 28, color: surface.accent, mb: 1 }} />
              <Typography sx={{ fontWeight: 700 }}>No saved templates yet</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: surface.muted, mt: 0.5 }}>
                Save one from the bar to customize this AI action.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.25} sx={{ mt: 0.5 }}>
              {kindTemplates.map((template) => {
                const active = previewTemplate?.id === template.id;
                return (
                  <Box
                    key={template.id}
                    onClick={() => openPreview(template)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '14px',
                      border: '1px solid',
                      borderColor: active ? surface.accent : surface.fieldBorder,
                      bgcolor: active ? surface.cardHover : surface.cardBg,
                      px: 1.5,
                      py: 1.25,
                      transition: 'border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease',
                      '&:hover': {
                        bgcolor: surface.cardHover,
                        borderColor: surface.accent,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {renameId === template.id ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={renameName}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => setRenameName(event.target.value)}
                            onBlur={commitRename}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                commitRename();
                              }
                            }}
                            disabled={busy}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                          />
                        ) : (
                          <>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              {template.is_default ? (
                                <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                              ) : (
                                <StarBorderIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                              )}
                              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700 }}>
                                {template.name}
                              </Typography>
                              {template.is_default ? (
                                <Chip size="small" label="Default" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                              ) : null}
                            </Stack>
                            <Typography sx={{ mt: 0.45, fontSize: '0.75rem', color: surface.muted }}>
                              {formatDate(template.updated_at || template.created_at)}
                              {' · '}
                              {previewText(template.system_prompt || template.user_prompt_template, 90)}
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.25} onClick={(event) => event.stopPropagation()}>
                        <Tooltip title={template.is_default ? 'Clear default' : 'Set as default'}>
                          <IconButton size="small" disabled={busy} onClick={() => handleSetDefault(template.id, !template.is_default)}>
                            {template.is_default ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Rename">
                          <IconButton
                            size="small"
                            disabled={busy}
                            onClick={() => {
                              setRenameId(template.id);
                              setRenameName(template.name);
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit prompts">
                          <IconButton size="small" disabled={busy} onClick={() => openEditDialog(template)}>
                            <SettingsOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" disabled={busy} onClick={() => handleDelete(template.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}

          {previewTemplate ? (
            <Box sx={{ mt: 2.25 }}>
              <Divider sx={{ mb: 2, borderColor: surface.fieldBorder }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.92rem' }}>
                  Preview · {previewTemplate.name}
                </Typography>
                <Button
                  size="small"
                  onClick={() => openEditDialog(previewTemplate)}
                  sx={{ textTransform: 'none', fontWeight: 700, color: surface.accent }}
                >
                  Edit
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                <PromptPreviewPanel
                  title="System prompt"
                  badge="saved"
                  value={previewTemplate.system_prompt || ''}
                  surface={surface}
                  isDarkMode={isDarkMode}
                />
                <PromptPreviewPanel
                  title="User prompt"
                  badge="saved"
                  value={previewTemplate.user_prompt_template || ''}
                  surface={surface}
                  isDarkMode={isDarkMode}
                />
              </Stack>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setManageOpen(false)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
